/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { navigate } from '../../../../helpers/history';
import { showDialog, showDialogAsync } from '../../../../components/dialog';
import { isHaveTeammatePermission } from '../../../helpers/user';
import { LocalStorage } from '../../../../shared/utils';
import localization from '../../../../shared/strings';

const LOCAL_STORAGE_AI_FEATURES_UPGRADE_MODAL_SHOWN_KEY = 'picsio.aiFeaturesUpgradeModalShown';

const sendAssetsToTranscribe = createAsyncThunk(
  'asset/sendToTranscribe',
  async (
    { assetIds, isBulkAIProcess, language },
    {
      rejectWithValue,
      dispatch,
      getState,
      extra: {
        sdk, Toast, utils, localization,
      },
    },
  ) => {
    const { user, roles, teammates } = getState();
    const billingDenied = !isHaveTeammatePermission('manageBilling', user);
    const keywordingDenied = !isHaveTeammatePermission('manageKeywords', user);
    const teamManagingDenied = !isHaveTeammatePermission('manageTeam', user);

    const canNotBuyApiCalls = billingDenied || keywordingDenied || teamManagingDenied;
    const usersWithPermissionsToBuyApiCalls = utils.getUsersWithPermissionToBuyKeywords(user, teammates.items, roles.items);

    const transcribeProcessing = () => {
      if (user?.addonsFeatures?.aiKit?.id) {
        return sdk.assets.transcribeSpeechToText(assetIds, language);
      }

      return sdk.assets.analyzeVideo(assetIds);
    };

    try {
      const { data } = await transcribeProcessing();

      if (!isBulkAIProcess) {
        if (assetIds.length >= 2) {
          Toast(localization.TRANSCRIPT.TOASTS.textTranscribeIPanelInProgress(assetIds.length), { closeButton: false });
        } else {
          Toast(localization.TRANSCRIPT.TOASTS.textTranscribeTPanelInProgress, { closeButton: false });
        }

        if (data.hasNotPermittedAssets) {
          Toast(localization.NO_COLLECTION_PERMISSION_TO_ACCESS('process audio and video with AI'), { autoClose: false });
        }
      }
      if (data?.hasUnsupportedAssets) {
        const { text, btnOk, link } = localization.TRANSCRIPT.TOASTS.unsupportedFormats;
        Toast(text, { btnOkValue: btnOk, onOk: () => navigate(link) });
      }

      return { assetIds };
    } catch (error) {
      const errorCode = error?.response?.status;
      const errorSubcode = utils.getDataFromResponceError(error, 'subcode');
      if (errorCode === 403) {
        showDialog({
          title: localization.ERROR,
          text: localization.NO_PERMISSION_TO_ACCESS,
          textBtnCancel: null,
        });
      } else if (errorSubcode === 'FeatureNotAvailableForPlanError') {
        showDialog({
          title: localization.DIALOGS.UPGRADE_AI_FEATURES.TITLE,
          text: localization.DIALOGS.UPGRADE_AI_FEATURES.TEXT,
          textBtnOk: localization.DIALOGS.UPGRADE_AI_FEATURES.BTN_OK,
          onOk() {
            navigate('/billing?tab=overview');
          },
          textBtnCancel: localization.DIALOGS.UPGRADE_AI_FEATURES.BTN_CANCEL,
        });
      } else if (errorSubcode === 'NotEnoughPaidApiCallsError') {
        if (canNotBuyApiCalls) {
          showDialog({
            title: localization.TRANSCRIPT.DIALOGS.titleNotEnoughAICredits,
            text: localization.TRANSCRIPT.DIALOGS.textNotEnoughAICredits(canNotBuyApiCalls, usersWithPermissionsToBuyApiCalls),
            textBtnOk: localization.DIALOGS.btnOk,
            onOk() {
              if (canNotBuyApiCalls) return;
              navigate('/billing?tab=overview');
            },
            textBtnCancel: null,
          });
        } else {
          showDialog({
            title: localization.DIALOGS.UPGRADE_AI_FEATURES.TITLE,
            text: localization.DIALOGS.UPGRADE_AI_FEATURES.TEXT,
            textBtnOk: localization.DIALOGS.UPGRADE_AI_FEATURES.BTN_OK,
            textBtnCancel: localization.DIALOGS.UPGRADE_AI_FEATURES.BTN_CANCEL,
            onOk() {
              navigate('/billing?tab=overview');
            },
          });
        }
      } else if (errorSubcode === 'AllAssetsNotSupportedForVideoAnalyzingError') {
        const { text, btnOk, link } = localization.TRANSCRIPT.TOASTS.unsupportedFormats;
        Toast(text, { btnOkValue: btnOk, onOk: () => navigate(link) });
      } else {
        Toast(localization.SOMETHING_WENT_WRONG.TEXT, {
          btnOkValue: localization.RETRY,
          onOk: () => dispatch(sendAssetsToTranscribe({ assetIds, isBulkAIProcess })),
        });
      }
      return rejectWithValue(error);
    }
  },
  {
    condition: async (payload, { getState, extra }) => {
      const { user } = getState();
      const { localization } = extra;
      if (LocalStorage.get(LOCAL_STORAGE_AI_FEATURES_UPGRADE_MODAL_SHOWN_KEY) || !user?.addonsFeatures?.aiKit?.id) {
        return true;
      }

      const { promise } = showDialogAsync({
        title: localization.DIALOGS.UPGRADE_AI_KIT_TO_UNLOCK_NEW_FEATURES.TITLE,
        text: localization.DIALOGS.UPGRADE_AI_KIT_TO_UNLOCK_NEW_FEATURES.TEXT,
        textBtnOk: localization.DIALOGS.UPGRADE_AI_KIT_TO_UNLOCK_NEW_FEATURES.BTN_OK,
        onOk() { navigate('/billing?tab=overview'); },
        onCancel() { },
        onClose() { },
        checkbox: {
          label: localization.DIALOGS.UPGRADE_AI_KIT_TO_UNLOCK_NEW_FEATURES.CHECKBOX_LABEL,
          onChange: (v) => LocalStorage.set(LOCAL_STORAGE_AI_FEATURES_UPGRADE_MODAL_SHOWN_KEY, v),
        },
        textBtnCancel: localization.DIALOGS.UPGRADE_AI_KIT_TO_UNLOCK_NEW_FEATURES.BTN_CANCEL,
      });

      try {
        await promise;
        return false;
      } catch (error) {
        if (error?.message === 'close') {
          return false;
        }
        return true;
      }
    },
  },
);

export default sendAssetsToTranscribe;
